import { useState, useEffect, useCallback } from 'react'
import { Loader2, LogIn, LogOut, Save, Search, Upload, Check, AlertCircle, Package, ChevronLeft, ChevronRight, KeyRound } from 'lucide-react'

// Portal self-service de proveedores asiáticos. Página standalone en INGLÉS.
// Usa fetch directo (NO el axios de lib/api con token de staff) y guarda su
// propio token en sessionStorage. Servida en suppliers.jvs-app.es.

const API_BASE = '/api/supplier-portal'
const TOKEN_KEY = 'jvs_supplier_portal_token'

interface Product {
  id: number
  supplier_reference: string
  our_reference: string | null
  name: string
  ean: string | null
  brand: string | null
  category: string | null
  cost_price: number
  stock: number
  stock_status: string | null
  image_url: string | null
  pending_submission: {
    submitted_cost_price: number | null
    submitted_stock: number | null
    submitted_stock_status: string | null
    created_at: string
  } | null
}

interface Draft {
  cost_price: string
  stock: string
  stock_status: string
  dirty: boolean
}

const STOCK_STATUSES = ['available', 'on_order', 'out_of_stock']
const PAGE_SIZE = 50

export default function SupplierPortalPage() {
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY))
  const [profile, setProfile] = useState<any>(null)

  // login state
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)

  // forgot / reset password
  const [resetToken, setResetToken] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('reset')
  )
  const [showForgot, setShowForgot] = useState(false)
  const [forgotIdentifier, setForgotIdentifier] = useState('')
  const [forgotSent, setForgotSent] = useState(false)
  const [forgotLoading, setForgotLoading] = useState(false)
  const [resetPw, setResetPw] = useState('')
  const [resetPwConfirm, setResetPwConfirm] = useState('')
  const [resetError, setResetError] = useState('')
  const [resetDone, setResetDone] = useState(false)

  // password change
  const [mustChange, setMustChange] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwError, setPwError] = useState('')

  // catalog
  const [products, setProducts] = useState<Product[]>([])
  const [drafts, setDrafts] = useState<Record<number, Draft>>({})
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState('')

  // excel
  const [showExcel, setShowExcel] = useState(false)

  // view: catalog | orders | propose
  const [view, setView] = useState<'catalog' | 'orders' | 'propose'>('catalog')

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  )

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY)
    setToken(null)
    setProfile(null)
    setProducts([])
  }, [])

  // --- load profile on token ---
  useEffect(() => {
    if (!token) return
    fetch(`${API_BASE}/auth/me`, { headers: authHeaders() })
      .then((r) => {
        if (!r.ok) throw new Error('unauthorized')
        return r.json()
      })
      .then((data) => {
        setProfile(data)
        setMustChange(!!data.must_change_password)
      })
      .catch(() => logout())
  }, [token, authHeaders, logout])

  // --- load catalog ---
  const loadProducts = useCallback(() => {
    if (!token || mustChange) return
    setLoading(true)
    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) })
    if (search.trim()) params.set('search', search.trim())
    fetch(`${API_BASE}/products?${params}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        setProducts(data.products || [])
        setTotal(data.total || 0)
        const d: Record<number, Draft> = {}
        for (const p of data.products || []) {
          const sub = p.pending_submission
          d[p.id] = {
            cost_price: String(sub?.submitted_cost_price ?? p.cost_price ?? ''),
            stock: String(sub?.submitted_stock ?? p.stock ?? ''),
            stock_status: sub?.submitted_stock_status ?? p.stock_status ?? 'available',
            dirty: false,
          }
        }
        setDrafts(d)
      })
      .finally(() => setLoading(false))
  }, [token, mustChange, page, search, authHeaders])

  useEffect(() => {
    loadProducts()
  }, [loadProducts])

  // --- handlers ---
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')
    setLoginLoading(true)
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || 'Invalid username or password')
      }
      const data = await res.json()
      sessionStorage.setItem(TOKEN_KEY, data.token)
      setToken(data.token)
      setProfile(data.supplier)
      setMustChange(!!data.supplier?.must_change_password)
    } catch (err: any) {
      setLoginError(err.message)
    } finally {
      setLoginLoading(false)
    }
  }

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault()
    setForgotLoading(true)
    try {
      await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username_or_email: forgotIdentifier }),
      })
      setForgotSent(true)
    } finally {
      setForgotLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setResetError('')
    if (resetPw.length < 6) return setResetError('Password must be at least 6 characters')
    if (resetPw !== resetPwConfirm) return setResetError('Passwords do not match')
    const res = await fetch(`${API_BASE}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: resetToken, new_password: resetPw }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      return setResetError(err.detail || 'Invalid or expired reset link')
    }
    setResetDone(true)
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPwError('')
    if (newPassword.length < 6) return setPwError('Password must be at least 6 characters')
    if (newPassword !== confirmPassword) return setPwError('Passwords do not match')
    const res = await fetch(`${API_BASE}/auth/change-password`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ current_password: password, new_password: newPassword }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      return setPwError(err.detail || 'Could not change password')
    }
    setMustChange(false)
    setPassword('')
    setNewPassword('')
    setConfirmPassword('')
  }

  const updateDraft = (id: number, field: keyof Draft, value: string) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value, dirty: true } }))
  }

  const dirtyCount = Object.values(drafts).filter((d) => d.dirty).length

  const handleSaveAll = async () => {
    const items = Object.entries(drafts)
      .filter(([, d]) => d.dirty)
      .map(([id, d]) => ({
        product_id: Number(id),
        cost_price: d.cost_price === '' ? null : Number(d.cost_price),
        stock: d.stock === '' ? null : Number(d.stock),
        stock_status: d.stock_status,
      }))
    if (!items.length) return
    setSaving(true)
    setSavedMsg('')
    try {
      const res = await fetch(`${API_BASE}/products/quotes/bulk`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ items }),
      })
      const data = await res.json()
      setSavedMsg(`Submitted ${data.created} quote(s) for review`)
      loadProducts()
    } finally {
      setSaving(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // ====================================================================
  // RENDER
  // ====================================================================

  // Reset password view (arriving from the email link ?reset=<token>)
  if (resetToken && !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="w-full max-w-sm bg-white rounded-xl shadow-lg p-8 space-y-5">
          <div className="text-center">
            <KeyRound className="mx-auto text-blue-600" size={28} />
            <h1 className="text-xl font-bold text-slate-800 mt-2">Set a new password</h1>
          </div>
          {resetDone ? (
            <>
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg">
                <Check size={16} /> Password updated. You can sign in now.
              </div>
              <button
                onClick={() => {
                  setResetToken(null)
                  window.history.replaceState({}, '', '/supplier-portal')
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 font-medium"
              >
                Go to sign in
              </button>
            </>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {resetError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  <AlertCircle size={16} /> {resetError}
                </div>
              )}
              <input
                type="password"
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                placeholder="New password"
                value={resetPw}
                onChange={(e) => setResetPw(e.target.value)}
              />
              <input
                type="password"
                className="w-full border border-slate-300 rounded-lg px-3 py-2"
                placeholder="Confirm new password"
                value={resetPwConfirm}
                onChange={(e) => setResetPwConfirm(e.target.value)}
              />
              <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 font-medium">
                Save password
              </button>
            </form>
          )}
        </div>
      </div>
    )
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <form onSubmit={showForgot ? handleForgot : handleLogin} className="w-full max-w-sm bg-white rounded-xl shadow-lg p-8 space-y-5">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-800">Supplier Portal</h1>
            <p className="text-sm text-slate-500 mt-1">JVS Informática — supplier quotes</p>
          </div>

          {showForgot ? (
            forgotSent ? (
              <>
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg">
                  <Check size={16} /> If the account exists, a reset email has been sent.
                </div>
                <button
                  type="button"
                  onClick={() => { setShowForgot(false); setForgotSent(false) }}
                  className="w-full text-sm text-blue-600 hover:underline"
                >
                  Back to sign in
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-500">Enter your username or email and we'll send a reset link.</p>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  placeholder="Username or email"
                  value={forgotIdentifier}
                  onChange={(e) => setForgotIdentifier(e.target.value)}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={forgotLoading || !forgotIdentifier}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {forgotLoading ? <Loader2 className="animate-spin" size={18} /> : null} Send reset link
                </button>
                <button type="button" onClick={() => setShowForgot(false)} className="w-full text-sm text-slate-500 hover:underline">
                  Back to sign in
                </button>
              </>
            )
          ) : (
            <>
              {loginError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  <AlertCircle size={16} /> {loginError}
                </div>
              )}
              <input
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
              />
              <input
                type="password"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loginLoading ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />} Sign in
              </button>
              <button type="button" onClick={() => setShowForgot(true)} className="w-full text-sm text-blue-600 hover:underline">
                Forgot password?
              </button>
            </>
          )}
        </form>
      </div>
    )
  }

  if (mustChange) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <form onSubmit={handleChangePassword} className="w-full max-w-sm bg-white rounded-xl shadow-lg p-8 space-y-5">
          <div className="text-center">
            <KeyRound className="mx-auto text-blue-600" size={28} />
            <h1 className="text-xl font-bold text-slate-800 mt-2">Set a new password</h1>
            <p className="text-sm text-slate-500 mt-1">Please choose a new password to continue</p>
          </div>
          {pwError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              <AlertCircle size={16} /> {pwError}
            </div>
          )}
          <input
            type="password"
            className="w-full border border-slate-300 rounded-lg px-3 py-2"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <input
            type="password"
            className="w-full border border-slate-300 rounded-lg px-3 py-2"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 font-medium">
            Save password
          </button>
        </form>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="text-blue-600" size={22} />
            <div>
              <h1 className="font-bold text-slate-800 leading-tight">Supplier Portal</h1>
              <p className="text-xs text-slate-500">{profile?.company_name || profile?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <nav className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              <button onClick={() => setView('catalog')} className={`px-3 py-1 rounded-md text-sm ${view === 'catalog' ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>Catalog</button>
              <button onClick={() => setView('orders')} className={`px-3 py-1 rounded-md text-sm ${view === 'orders' ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>Orders</button>
              <button onClick={() => setView('propose')} className={`px-3 py-1 rounded-md text-sm ${view === 'propose' ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>New product</button>
            </nav>
            <span className="text-xs text-slate-500">Prices in {profile?.quote_currency || 'USD'}</span>
            <button onClick={logout} className="text-slate-500 hover:text-red-600 flex items-center gap-1 text-sm">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      {view === 'orders' ? (
        <main className="max-w-7xl mx-auto px-4 py-6">
          <OrdersView authHeaders={authHeaders} token={token} />
        </main>
      ) : view === 'propose' ? (
        <main className="max-w-7xl mx-auto px-4 py-6">
          <ProposeView authHeaders={authHeaders} token={token} />
        </main>
      ) : (
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              className="pl-9 pr-3 py-2 border border-slate-300 rounded-lg w-72"
              placeholder="Search reference, name, EAN…"
              value={search}
              onChange={(e) => {
                setPage(1)
                setSearch(e.target.value)
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowExcel((v) => !v)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm flex items-center gap-1 hover:bg-slate-50"
            >
              <Upload size={16} /> Upload Excel
            </button>
            <button
              onClick={handleSaveAll}
              disabled={!dirtyCount || saving}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              Save all{dirtyCount ? ` (${dirtyCount})` : ''}
            </button>
          </div>
        </div>

        {savedMsg && (
          <div className="mb-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg">
            <Check size={16} /> {savedMsg}
          </div>
        )}

        {showExcel && <ExcelUpload authHeaders={authHeaders} onDone={loadProducts} />}

        {/* Catalog table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400">
              <Loader2 className="animate-spin mx-auto" size={28} />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-left">
                  <tr>
                    <th className="px-3 py-3 font-medium">Image</th>
                    <th className="px-3 py-3 font-medium">Reference</th>
                    <th className="px-3 py-3 font-medium">Product</th>
                    <th className="px-3 py-3 font-medium">EAN</th>
                    <th className="px-3 py-3 font-medium">Price ({profile?.quote_currency || 'USD'})</th>
                    <th className="px-3 py-3 font-medium">Stock</th>
                    <th className="px-3 py-3 font-medium">Availability</th>
                    <th className="px-3 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {products.map((p) => {
                    const d = drafts[p.id]
                    if (!d) return null
                    return (
                      <tr key={p.id} className={d.dirty ? 'bg-amber-50' : ''}>
                        <td className="px-3 py-2">
                          {p.image_url ? (
                            <img src={p.image_url} alt="" className="w-12 h-12 object-contain rounded border border-slate-100" />
                          ) : (
                            <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center text-slate-300">
                              <Package size={18} />
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-700">{p.supplier_reference}</td>
                        <td className="px-3 py-2 max-w-xs">
                          <div className="text-slate-800">{p.name}</div>
                          {p.brand && <div className="text-xs text-slate-400">{p.brand}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{p.ean || '—'}</td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-24 border border-slate-300 rounded px-2 py-1"
                            value={d.cost_price}
                            onChange={(e) => updateDraft(p.id, 'cost_price', e.target.value)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            className="w-20 border border-slate-300 rounded px-2 py-1"
                            value={d.stock}
                            onChange={(e) => updateDraft(p.id, 'stock', e.target.value)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            className="border border-slate-300 rounded px-2 py-1"
                            value={d.stock_status}
                            onChange={(e) => updateDraft(p.id, 'stock_status', e.target.value)}
                          >
                            {STOCK_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s.replace('_', ' ')}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          {p.pending_submission ? (
                            <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full whitespace-nowrap">
                              Pending review
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                  {!products.length && (
                    <tr>
                      <td colSpan={8} className="px-3 py-12 text-center text-slate-400">
                        No products assigned yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="p-2 rounded border border-slate-300 disabled:opacity-40"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm text-slate-600">
              Page {page} / {totalPages} · {total} products
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="p-2 rounded border border-slate-300 disabled:opacity-40"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </main>
      )}
    </div>
  )
}

// ----------------------------------------------------------------------
// Orders view (purchase orders received from JVS) — list + confirm
// ----------------------------------------------------------------------
const ORDER_STATUS: Record<string, string> = {
  sent: 'New — please confirm', supplier_review: 'In review', confirmed: 'Confirmed', ordered: 'Ordered',
}

function OrdersView({ authHeaders, token }: { authHeaders: () => Record<string, string>; token: string | null }) {
  const [orders, setOrders] = useState<any[]>([])
  const [selected, setSelected] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [lines, setLines] = useState<Record<number, { qty: string; price: string; lead: string; note: string }>>({})
  const [supplierNote, setSupplierNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState('')

  const loadOrders = () => {
    setLoading(true)
    fetch(`${API_BASE}/orders`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setOrders(d.orders || []))
      .finally(() => setLoading(false))
  }
  useEffect(loadOrders, [])

  const openOrder = (id: number) => {
    fetch(`${API_BASE}/orders/${id}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((o) => {
        setSelected(o)
        setSupplierNote(o.supplier_note || '')
        const l: Record<number, any> = {}
        for (const ln of o.lines) {
          l[ln.id] = {
            qty: String(ln.qty_confirmed ?? ln.qty_requested ?? ''),
            price: String(ln.unit_price_confirmed_usd ?? ln.unit_price_usd ?? ''),
            lead: String(ln.lead_time_days ?? ''),
            note: ln.supplier_line_note ?? '',
          }
        }
        setLines(l)
      })
  }

  const submitConfirm = async () => {
    if (!selected) return
    setSaving(true)
    setSavedMsg('')
    try {
      const payload = {
        supplier_note: supplierNote,
        lines: Object.entries(lines).map(([id, v]) => ({
          line_id: Number(id),
          qty_confirmed: v.qty === '' ? null : Number(v.qty),
          unit_price_confirmed_usd: v.price === '' ? null : Number(v.price),
          lead_time_days: v.lead === '' ? null : Number(v.lead),
          note: v.note || null,
        })),
      }
      const res = await fetch(`${API_BASE}/orders/${selected.id}/confirm`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      })
      if (res.ok) {
        setSavedMsg('Order confirmed. Thank you!')
        loadOrders()
        openOrder(selected.id)
      }
    } finally {
      setSaving(false)
    }
  }

  if (selected) {
    const editable = ['sent', 'supplier_review', 'confirmed'].includes(selected.status)
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <button onClick={() => setSelected(null)} className="text-sm text-blue-600 mb-4">&larr; Back to orders</button>
        <h2 className="font-bold text-slate-800 mb-1">Order #{selected.id}</h2>
        <p className="text-sm text-slate-500 mb-4">{ORDER_STATUS[selected.status] || selected.status}</p>
        {savedMsg && <div className="mb-4 flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg"><Check size={16} /> {savedMsg}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-2 py-2">Reference</th><th className="px-2 py-2">Product</th>
                <th className="px-2 py-2">Requested</th><th className="px-2 py-2">Available qty</th>
                <th className="px-2 py-2">Price USD</th><th className="px-2 py-2">Lead time (days)</th><th className="px-2 py-2">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {selected.lines.map((l: any) => (
                <tr key={l.id}>
                  <td className="px-2 py-2 font-mono text-xs">{l.supplier_reference}</td>
                  <td className="px-2 py-2">{l.name}</td>
                  <td className="px-2 py-2">{l.qty_requested}</td>
                  <td className="px-2 py-2"><input disabled={!editable} type="number" min="0" className="w-20 border border-slate-300 rounded px-2 py-1 disabled:bg-slate-50" value={lines[l.id]?.qty || ''} onChange={(e) => setLines({ ...lines, [l.id]: { ...lines[l.id], qty: e.target.value } })} /></td>
                  <td className="px-2 py-2"><input disabled={!editable} type="number" step="0.01" min="0" className="w-24 border border-slate-300 rounded px-2 py-1 disabled:bg-slate-50" value={lines[l.id]?.price || ''} onChange={(e) => setLines({ ...lines, [l.id]: { ...lines[l.id], price: e.target.value } })} /></td>
                  <td className="px-2 py-2"><input disabled={!editable} type="number" min="0" className="w-16 border border-slate-300 rounded px-2 py-1 disabled:bg-slate-50" value={lines[l.id]?.lead || ''} onChange={(e) => setLines({ ...lines, [l.id]: { ...lines[l.id], lead: e.target.value } })} /></td>
                  <td className="px-2 py-2"><input disabled={!editable} className="w-36 border border-slate-300 rounded px-2 py-1 disabled:bg-slate-50" value={lines[l.id]?.note || ''} onChange={(e) => setLines({ ...lines, [l.id]: { ...lines[l.id], note: e.target.value } })} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {editable && (
          <div className="mt-4 space-y-3">
            <textarea className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" rows={2} placeholder="Note to JVS (optional)" value={supplierNote} onChange={(e) => setSupplierNote(e.target.value)} />
            <button onClick={submitConfirm} disabled={saving} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Confirm order
            </button>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-slate-100">
          <h3 className="text-sm font-semibold text-slate-600 mb-2">Messages</h3>
          <PortalMessageThread authHeaders={authHeaders} token={token} context={{ order_id: selected.id }} />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {loading ? (
        <div className="p-12 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" size={28} /></div>
      ) : !orders.length ? (
        <div className="p-12 text-center text-slate-400">No purchase orders yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr><th className="px-3 py-3">Order</th><th className="px-3 py-3">Status</th><th className="px-3 py-3">Lines</th><th className="px-3 py-3">Date</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {orders.map((o) => (
              <tr key={o.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openOrder(o.id)}>
                <td className="px-3 py-3 font-medium">#{o.id}</td>
                <td className="px-3 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${o.status === 'sent' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{ORDER_STATUS[o.status] || o.status}</span>
                </td>
                <td className="px-3 py-3">{o.line_count}</td>
                <td className="px-3 py-3 text-slate-400">{String(o.created_at).slice(0, 10)}</td>
                <td className="px-3 py-3 text-right text-blue-600">Open &rarr;</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ----------------------------------------------------------------------
// Message thread (supplier side) — chat + attachments for a context
// ----------------------------------------------------------------------
function PortalMessageThread({
  authHeaders, token, context,
}: {
  authHeaders: () => Record<string, string>
  token: string | null
  context: { product_id?: number; order_id?: number; proposal_id?: number }
}) {
  const [messages, setMessages] = useState<any[]>([])
  const [body, setBody] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [sending, setSending] = useState(false)

  const qs = new URLSearchParams()
  if (context.order_id) qs.set('order_id', String(context.order_id))
  if (context.product_id) qs.set('product_id', String(context.product_id))
  if (context.proposal_id) qs.set('proposal_id', String(context.proposal_id))

  const load = () => {
    fetch(`${API_BASE}/messages?${qs}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
  }
  useEffect(load, [context.order_id, context.product_id, context.proposal_id])

  const send = async () => {
    if (!body && !file) return
    setSending(true)
    try {
      const fd = new FormData()
      if (body) fd.append('body', body)
      if (context.order_id) fd.append('order_id', String(context.order_id))
      if (context.product_id) fd.append('product_id', String(context.product_id))
      if (context.proposal_id) fd.append('proposal_id', String(context.proposal_id))
      if (file) fd.append('files', file)
      const { Authorization } = authHeaders()
      await fetch(`${API_BASE}/messages`, { method: 'POST', headers: { Authorization }, body: fd })
      setBody(''); setFile(null); load()
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      <div className="space-y-2 max-h-64 overflow-y-auto mb-3">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender === 'supplier' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.sender === 'supplier' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-800'}`}>
              {m.body && <div className="whitespace-pre-wrap">{m.body}</div>}
              {m.attachments?.map((a: any) => (
                <a key={a.id} href={`${API_BASE}/attachments/${a.id}?token=${token}`} target="_blank" rel="noreferrer"
                  className={`block text-xs underline mt-1 ${m.sender === 'supplier' ? 'text-blue-100' : 'text-blue-600'}`}>
                  📎 {a.filename}
                </a>
              ))}
              <div className={`text-[10px] mt-1 ${m.sender === 'supplier' ? 'text-blue-200' : 'text-slate-400'}`}>{String(m.created_at).slice(0, 16).replace('T', ' ')}</div>
            </div>
          </div>
        ))}
        {!messages.length && <p className="text-xs text-slate-400">No messages yet.</p>}
      </div>
      <div className="flex items-center gap-2">
        <input className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Type a message…" value={body}
          onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} />
        <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs w-28" />
        <button onClick={send} disabled={sending || (!body && !file)} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  )
}

// ----------------------------------------------------------------------
// Propose new product (supplier offers a product not in our catalog)
// ----------------------------------------------------------------------
function ProposeView({ authHeaders, token }: { authHeaders: () => Record<string, string>; token: string | null }) {
  const [form, setForm] = useState<any>({})
  const [file, setFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const [proposals, setProposals] = useState<any[]>([])

  const load = () => {
    fetch(`${API_BASE}/proposals`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setProposals(d.proposals || []))
  }
  useEffect(load, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name) return
    setSaving(true)
    setMsg('')
    try {
      const fd = new FormData()
      fd.append('name', form.name)
      for (const k of ['supplier_reference', 'description', 'ean', 'brand', 'category', 'cost_price_usd', 'stock']) {
        if (form[k]) fd.append(k, form[k])
      }
      if (file) fd.append('image', file)
      const { Authorization } = authHeaders()
      const res = await fetch(`${API_BASE}/proposals`, { method: 'POST', headers: { Authorization }, body: fd })
      if (res.ok) {
        setMsg('Product submitted for review. Thank you!')
        setForm({})
        setFile(null)
        load()
      }
    } finally {
      setSaving(false)
    }
  }

  const statusBadge = (s: string) =>
    s === 'accepted' ? 'bg-green-100 text-green-700' : s === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-sm p-6 space-y-3">
        <h2 className="font-semibold text-slate-800">Propose a new product</h2>
        <p className="text-sm text-slate-500">Offer a product that isn't in our catalog yet. Our team will review it.</p>
        {msg && <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg"><Check size={16} /> {msg}</div>}
        <input className="w-full border border-slate-300 rounded-lg px-3 py-2" placeholder="Product name *" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <input className="border border-slate-300 rounded-lg px-3 py-2" placeholder="Your reference" value={form.supplier_reference || ''} onChange={(e) => setForm({ ...form, supplier_reference: e.target.value })} />
          <input className="border border-slate-300 rounded-lg px-3 py-2" placeholder="EAN" value={form.ean || ''} onChange={(e) => setForm({ ...form, ean: e.target.value })} />
          <input className="border border-slate-300 rounded-lg px-3 py-2" placeholder="Brand" value={form.brand || ''} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
          <input className="border border-slate-300 rounded-lg px-3 py-2" placeholder="Category" value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <input type="number" step="0.01" className="border border-slate-300 rounded-lg px-3 py-2" placeholder="Price USD" value={form.cost_price_usd || ''} onChange={(e) => setForm({ ...form, cost_price_usd: e.target.value })} />
          <input type="number" className="border border-slate-300 rounded-lg px-3 py-2" placeholder="Stock" value={form.stock || ''} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
        </div>
        <textarea className="w-full border border-slate-300 rounded-lg px-3 py-2" rows={2} placeholder="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div>
          <label className="text-sm text-slate-600">Photo</label>
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block text-sm mt-1" />
        </div>
        <button type="submit" disabled={saving || !form.name} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">
          {saving ? <Loader2 className="animate-spin" size={16} /> : null} Submit product
        </button>
      </form>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="font-semibold text-slate-800 mb-3">Your proposals</h2>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {proposals.map((p) => (
            <div key={p.id} className="flex items-center gap-3 border border-slate-100 rounded-lg p-2">
              {p.has_image && token ? (
                <img src={`${API_BASE}/proposals/${p.id}/image?token=${token}`} alt="" className="w-12 h-12 object-contain rounded" />
              ) : (
                <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center text-slate-300"><Package size={18} /></div>
              )}
              <div className="flex-1">
                <div className="text-sm text-slate-800">{p.name}</div>
                <div className="text-xs text-slate-400">{p.cost_price_usd ? `$${p.cost_price_usd}` : ''} {p.supplier_reference || ''}</div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${statusBadge(p.status)}`}>{p.status}</span>
            </div>
          ))}
          {!proposals.length && <p className="text-sm text-slate-400">No proposals yet.</p>}
        </div>
      </div>
    </div>
  )
}

// ----------------------------------------------------------------------
// Excel upload sub-component (preview + column mapping + submit)
// ----------------------------------------------------------------------
function ExcelUpload({
  authHeaders,
  onDone,
}: {
  authHeaders: () => Record<string, string>
  onDone: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<{ headers: string[]; rows: string[][] } | null>(null)
  const [mapping, setMapping] = useState<{ reference: number; cost_price: number; stock: number }>({
    reference: 0,
    cost_price: 1,
    stock: 2,
  })
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string>('')

  const doPreview = async (f: File) => {
    setFile(f)
    setResult('')
    const fd = new FormData()
    fd.append('file', f)
    setBusy(true)
    try {
      // auth header only (let browser set multipart Content-Type)
      const { Authorization } = authHeaders()
      const res = await fetch(`${API_BASE}/import/preview`, {
        method: 'POST',
        headers: { Authorization },
        body: fd,
      })
      const data = await res.json()
      setPreview({ headers: data.headers || [], rows: (data.rows || []).slice(0, 5) })
    } finally {
      setBusy(false)
    }
  }

  const doSubmit = async () => {
    if (!file) return
    setBusy(true)
    setResult('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('column_mapping', JSON.stringify(mapping))
      const { Authorization } = authHeaders()
      const res = await fetch(`${API_BASE}/import/submit`, {
        method: 'POST',
        headers: { Authorization },
        body: fd,
      })
      const data = await res.json()
      setResult(`Submitted ${data.created} quote(s), ${data.unmatched} reference(s) not recognized`)
      onDone()
    } finally {
      setBusy(false)
    }
  }

  const cols = preview?.headers.map((h, i) => ({ label: `${i}: ${h}`, value: i })) || []

  return (
    <div className="mb-6 bg-white rounded-xl shadow-sm p-5 border border-blue-100">
      <h3 className="font-semibold text-slate-800 mb-1">Upload price list (Excel / CSV)</h3>
      <p className="text-xs text-slate-500 mb-3">
        Rows are matched to your assigned products by reference. Matches become pending quotes for review.
      </p>
      <input
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={(e) => e.target.files?.[0] && doPreview(e.target.files[0])}
        className="text-sm"
      />
      {busy && <Loader2 className="animate-spin inline ml-3 text-slate-400" size={16} />}

      {preview && (
        <div className="mt-4">
          <div className="grid grid-cols-3 gap-3 mb-3">
            {(['reference', 'cost_price', 'stock'] as const).map((field) => (
              <label key={field} className="text-sm">
                <span className="block text-slate-600 mb-1 capitalize">{field.replace('_', ' ')} column</span>
                <select
                  className="w-full border border-slate-300 rounded px-2 py-1"
                  value={mapping[field]}
                  onChange={(e) => setMapping((m) => ({ ...m, [field]: Number(e.target.value) }))}
                >
                  {cols.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <div className="overflow-x-auto border border-slate-100 rounded mb-3">
            <table className="text-xs">
              <tbody>
                {preview.rows.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {row.map((cell, j) => (
                      <td key={j} className="px-2 py-1 text-slate-600 whitespace-nowrap">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            onClick={doSubmit}
            disabled={busy}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            Submit price list
          </button>
        </div>
      )}
      {result && (
        <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg">
          <Check size={16} /> {result}
        </div>
      )}
    </div>
  )
}
